/**
 * Image Capture Component
 *
 * Permite capturar imagen desde cámara o seleccionar archivo.
 * Muestra preview de la imagen seleccionada.
 */

import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ImageCaptureProps {
  value?: File | string | null;
  onChange: (file: File | null) => void;
  existingUrl?: string | null;
  className?: string;
  disabled?: boolean;
}

export function ImageCapture({
  value,
  onChange,
  existingUrl,
  className,
  disabled = false,
}: ImageCaptureProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isExistingCleared, setIsExistingCleared] = useState(false);

  // Reset state when value changes to null (form reset) or when existingUrl changes (different patient)
  useEffect(() => {
    if (value === null || value === undefined) {
      setPreviewUrl(null);
    }
  }, [value]);

  // Reset isExistingCleared when existingUrl changes (editing different patient)
  useEffect(() => {
    setIsExistingCleared(false);
  }, [existingUrl]);

  // Cleanup: stop camera stream when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Determine what to show as preview
  // If user explicitly cleared the existing image, don't fall back to existingUrl
  const displayUrl =
    previewUrl ||
    (typeof value === "string" ? value : null) ||
    (isExistingCleared ? null : existingUrl);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onChange(file);
        setPreviewUrl(URL.createObjectURL(file));
        setIsExistingCleared(false); // Reset since we have a new image
      }
    },
    [onChange]
  );

  const openCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      setIsCameraOpen(true);

      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play();
        }
      }, 100);
    } catch {
      // Camera not available, fallback to file input
      fileInputRef.current?.click();
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return;

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `documento_${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          onChange(file);
          setPreviewUrl(URL.createObjectURL(file));
          setIsExistingCleared(false); // Reset since we have a new image
        }
        closeCamera();
      },
      "image/jpeg",
      0.9
    );
  }, [onChange, closeCamera]);

  const clearImage = useCallback(() => {
    onChange(null);
    setPreviewUrl(null);
    setIsExistingCleared(true); // Mark that user explicitly cleared the existing image
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [onChange]);

  return (
    <div className={cn("space-y-2", className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Camera view */}
      {isCameraOpen && (
        <div className="relative rounded-lg overflow-hidden bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-48 object-cover" />
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
            <Button type="button" size="sm" onClick={capturePhoto}>
              <Camera className="mr-2 h-4 w-4" />
              {t("common.capture")}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={closeCamera}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      {/* Preview or placeholder */}
      {!isCameraOpen && (
        <div
          className={cn(
            "relative rounded-lg border-2 border-dashed p-4 transition-colors",
            displayUrl ? "border-primary/50 bg-primary/5" : "border-muted-foreground/25",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          {displayUrl ? (
            <div className="relative">
              <img
                src={displayUrl}
                alt={t("pacientes.fields.documentPhoto")}
                className="w-full h-32 object-contain rounded"
              />
              {!disabled && (
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={clearImage}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-4 text-muted-foreground">
              <ImageIcon className="h-8 w-8 mb-2" />
              <p className="text-sm">{t("pacientes.fields.noDocumentPhoto")}</p>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {!isCameraOpen && !disabled && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" className="flex-1" onClick={openCamera}>
            <Camera className="mr-2 h-4 w-4" />
            {t("pacientes.fields.takePhoto")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t("pacientes.fields.uploadPhoto")}
          </Button>
        </div>
      )}
    </div>
  );
}
